import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';

import { useTranslate } from '@/hooks/useTranslate';

import { getDataTestId } from '@/utils/browser';
import { getObjectFieldByPath } from '@/utils/string';

import { Icon } from '../Icon/Icon';

import { SearchTextField } from './Form.styled';

type TextInputProps<FormPayload extends FieldValues> = {
  value?: string;
  control?: Control<FormPayload>;
  name?: Path<FormPayload>;
  rules?: Omit<
    RegisterOptions<FormPayload, Path<FormPayload>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  template?: 'search' | 'currency';
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
} & Omit<TextFieldProps, 'variant' | 'name'>;

export function TextInput<FormPayload extends FieldValues>({
  control,
  rules,
  name,
  template,
  error,
  helperText,
  type,
  value,
  placeholder,
  onChange,
  onBlur,
  startAdornment,
  endAdornment,
  ...props
}: TextInputProps<FormPayload>) {
  const [passwordType, setPasswordType] =
    useState<TextFieldProps['type']>('password');
  const { t } = useTranslate();

  const togglePasswordVisibility = () =>
    setPasswordType(prev => (prev === 'password' ? 'text' : 'password'));

  const render = useCallback(
    ({
      value,
      onChange,
      onBlur,
      defaultValue,
      isDirty,
    }: {
      value: TextFieldProps['value'];
      onChange: TextFieldProps['onChange'];
      onBlur: TextFieldProps['onBlur'];
      defaultValue?: TextFieldProps['defaultValue'];
      isDirty?: boolean;
    }) => {
      const isPasswordInput = type === 'password';

      const textFieldProps: TextFieldProps = {
        ...getDataTestId(`${placeholder || name || 'text'}-input`),
        name,
        type: isPasswordInput ? passwordType : type,
        value: typeof value === 'string' || isDirty ? value : defaultValue,
        onChange: onChange,
        onBlur: onBlur,
        error,
        helperText: error ? (
          <Stack gap={0.5} component="span">
            <Icon
              name="alert"
              size={20}
              color={(theme: Theme) => theme.palette.error.main}
            />
            {helperText}
          </Stack>
        ) : (
          helperText
        ),
        InputProps: {
          startAdornment:
            template === 'currency' && startAdornment !== undefined ? (
              <InputAdornment position="start">{startAdornment}</InputAdornment>
            ) : template === 'search' ? (
              <InputAdornment position="start">
                <Icon
                  name="search"
                  size={20}
                  color={(theme: Theme) => theme.palette.grey[700]}
                />
              </InputAdornment>
            ) : undefined,
          endAdornment: isPasswordInput ? (
            <InputAdornment position="end">
              <IconButton
                {...getDataTestId(
                  `${placeholder || name || 'password'}-visibility-toggle`,
                )}
                title={
                  passwordType === 'password'
                    ? t('common.showPassword')
                    : t('common.hidePassword')
                }
                onClick={togglePasswordVisibility}
                edge="end"
                size="small"
              >
                <Icon
                  name={passwordType === 'password' ? 'eye-open' : 'eye-close'}
                  color={(theme: Theme) => theme.palette.grey[700]}
                />
              </IconButton>
            </InputAdornment>
          ) : endAdornment ? (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ) : null,
        },
        placeholder:
          placeholder ||
          (template === 'search' ? t('common.search') : undefined),
        ...props,
      };

      return template === 'search' ? (
        <SearchTextField {...textFieldProps} />
      ) : (
        <TextField {...textFieldProps} />
      );
    },
    [
      t,
      startAdornment,
      endAdornment,
      error,
      helperText,
      name,
      passwordType,
      placeholder,
      props,
      template,
      type,
    ],
  );

  if (control && name) {
    return (
      <Controller
        rules={rules}
        control={control}
        name={name}
        render={({
          field: { onChange: fieldOnChange, onBlur: fieldOnBlur, value },
          fieldState: { isDirty },
          formState: { defaultValues },
        }) => {
          return render({
            value,
            onChange: e => {
              fieldOnChange(e);
              onChange?.(e);
            },
            onBlur: e => {
              fieldOnBlur();
              onBlur?.(e);
            },
            defaultValue: getObjectFieldByPath(defaultValues, name),
            isDirty,
          });
        }}
      />
    );
  }

  return render({ value, onChange, onBlur });
}
